import type { Cart } from '@iheartjane/data-access';
import type { GeocodingResult } from '@iheartjane/geolocation';
import type { Address, AppMode } from '@iheartjane/types';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import { createContext } from 'react';
import type { ReactNode } from 'react';

import type { CheckoutNewRequest } from '@jane/shared-ecomm/types';
/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial } from '@jane/shared/models';
import type {
  CartLineItems,
  CheckoutAgreementVersion,
  Identification,
  Store,
  Tags,
} from '@jane/shared/types';

import type {
  Customer,
  PreAuthCustomer,
  PreAuthOptions,
  PreAuthSelection,
  PrepaymentProviders,
} from './checkoutProvider.types';

export interface CheckoutContextType {
  appInfo: {
    appMode: AppMode;
    bloomUserExperience?: boolean;
    checkoutError: string | null;
    disableAuthFeatures?: boolean;
    disablePrepayment?: boolean;
    externalAuth?: boolean;
    headlessCheckoutPartnerId?: string;
    headlessLoginUrl?: string;
    isChangingReservationModes?: boolean;
    isCheckingOut?: boolean;
    isEmbeddedMode: boolean;
    isMobile?: boolean;
    janeDeviceId?: string;
    partnerHostedConfig: {
      isPartnerHosted: boolean;
      partnerHostedPath?: string;
    };
  };
  bagPickupMode: 'pickup' | 'curbside';
  cart: Cart;
  cartLineItems: CartLineItems;
  checkoutRequirements: {
    data?: {
      checkout_agreement_versions: CheckoutAgreementVersion[];
      medical_documentation_requirement:
        'photo' | 'number' | 'photo_and_number' | 'none' | null;
      medical_expiration_date_required?: boolean;
    } | null;
    loading: boolean;
  };
  customer: Customer;
  deliveryAddress: Address | null;
  deliveryValidation: {
    data: {
      can_deliver: boolean | null;
      delivers_today: boolean | null;
      fee: number | null;
      last_call_minutes: number | null;
      lead_time_minutes: number | null;
      message: string | null;
      minimum: number | null;
    };
    loading: boolean;
  };
  dispatches: {
    changeReservationMode: (mode: 'delivery' | 'pickup') => void;
    checkout: (payload: CheckoutNewRequest) => void;
    checkoutCancelled: () => void;
    checkoutError: (error: string) => void;
    checkoutStarted: () => void;
    dismissCheckoutError: () => void;
    refreshCart: () => void;
    setBagPickupMode: (mode: 'pickup' | 'curbside') => void;
    updateCustomerPickupAddress: (values: {
      errorMessage?: string | null;
      googleValidatedAddress?: GeocodingResult | null;
      selectedPickupAddress?: string | null;
    }) => void;
    /** `null` clears it. */
    updateGroupSpecial?: (groupSpecialId: number | null) => void;
    updateIdentification: ({
      document,
      file,
    }: {
      document: keyof Identification;
      file: string | null;
    }) => void;
    updatePaymentMethod: (paymentMethod: string | null) => void;
    updateTip: (value: number) => void;
    updateUser: (values: {
      birthDate?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      promotionsOptOut?: boolean;
    }) => void;
  };
  googleValidatedAddress: GeocodingResult | null;
  groupSpecials: StoreSpecial[];
  guestCheckout: {
    preAuthSelection: PreAuthSelection;
    setPreAuthCustomer: (customer: PreAuthCustomer) => void;
    setPreAuthOptions: (options: PreAuthOptions) => void;
    setPreAuthSelection: (selection: PreAuthSelection) => void;
    setShowLoginModal: (visible: boolean) => void;
    showLoginModal: boolean;
  };
  identification: {
    document: Identification;
    isUploading: boolean;
  };
  pickupAddressErrorMessage: string | null | undefined;
  prepaymentProviders: PrepaymentProviders[];
  selectedPickupAddress: string | null;
  store: Store;
  tags?: Tags;
  userGroupSpecialId?: string | number;
}

const CheckoutContext = createContext<CheckoutContextType | null>(null);
CheckoutContext.displayName = 'Checkout';

export const useCheckout = () => useContextEnsuringProvider(CheckoutContext);

interface Props {
  children: ReactNode;
  value: CheckoutContextType;
}

export const CheckoutProvider = ({ children, value }: Props) => {
  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};
